import { Controller } from 'react-hook-form';
import type { Control, FieldValues, Path } from 'react-hook-form';
import { Input, Label, Select } from '@/shared/components';

interface VatCostFieldsProps<T extends FieldValues> {
    control: Control<T>;
    netField: Path<T>;
    grossField: Path<T>;
    categoryField?: Path<T>;
    categories: { id: string; name: string }[];
    isLoadingCategories?: boolean;
    hideCategory?: boolean;
    disabled?: boolean;
    fieldId?: (name: string) => string;
    labels?: { category?: string; net?: string; gross?: string };
    /** Whether the net label carries the required marker. Off for optional overrides (e.g. NullableMoney). */
    netRequired?: boolean;
}

/** Presentational only — the owning form calls `useVatDerivedCost` to keep gross in sync. */
export function VatCostFields<T extends FieldValues>({
    control,
    netField,
    grossField,
    categoryField,
    categories,
    isLoadingCategories,
    hideCategory,
    disabled,
    fieldId = (name) => name,
    labels,
    netRequired = true,
}: VatCostFieldsProps<T>) {
    return (
        <>
            {!hideCategory && categoryField && (
                <div>
                    <Label htmlFor={fieldId(categoryField)} required>{labels?.category ?? 'VAT Category'}</Label>
                    <Controller
                        name={categoryField}
                        control={control}
                        render={({ field, fieldState: { error } }) => (
                            <Select
                                id={fieldId(categoryField)}
                                placeholder="Select VAT category"
                                value={field.value as string}
                                onChange={field.onChange}
                                onBlur={field.onBlur}
                                disabled={disabled}
                                error={!!error}
                                hint={error?.message}
                                isLoading={isLoadingCategories}
                                options={categories.map((c) => ({ value: c.id, label: c.name }))}
                            />
                        )}
                    />
                </div>
            )}

            <div>
                <Label htmlFor={fieldId(netField)} required={netRequired}>{labels?.net ?? 'Net (£)'}</Label>
                <Controller
                    name={netField}
                    control={control}
                    render={({ field, fieldState: { error } }) => (
                        <Input
                            id={fieldId(netField)}
                            type="number"
                            min="0"
                            step={0.0001}
                            value={field.value === undefined || field.value === null ? '' : String(field.value)}
                            onChange={(e) => field.onChange(e.target.value === '' ? null : e.target.value)}
                            onBlur={field.onBlur}
                            disabled={disabled}
                            error={!!error}
                            hint={error?.message}
                            placeholder="0.00"
                        />
                    )}
                />
            </div>

            <div>
                <Label htmlFor={fieldId(grossField)}>{labels?.gross ?? 'Gross (£)'}</Label>
                <Controller
                    name={grossField}
                    control={control}
                    render={({ field, fieldState: { error } }) => (
                        <Input
                            id={fieldId(grossField)}
                            type="number"
                            readOnly
                            disabled={disabled}
                            value={field.value === undefined || field.value === null ? '' : String(field.value)}
                            error={!!error}
                            hint={error?.message}
                            placeholder="0.00"
                        />
                    )}
                />
            </div>
        </>
    );
}
